import React, { useEffect, useRef, useState } from 'react';
import { validateTimeRange } from '../../utils/timeUtils';

const TimePicker = ({ 
  isOpen, 
  onClose, 
  onTimeSelect, 
  initialTime = { hours: 0, minutes: 0, period: 'AM' },
  title = "Select Time",
  otherTime = null, // The other time value to validate against
  isStartTime = true // Whether this is selecting start or end time
}) => {
  const [selectedTime, setSelectedTime] = useState(initialTime);
  const modalRef = useRef(null);

  useEffect(() => {
    setSelectedTime(initialTime);
  }, [initialTime]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleTimeChange = (field, value) => {
    setSelectedTime(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validate the current time selection
  const getValidationError = () => {
    if (!otherTime) return null;
    
    const startTime = isStartTime ? selectedTime : otherTime;
    const endTime = isStartTime ? otherTime : selectedTime;
    
    const validation = validateTimeRange(startTime, endTime);
    return validation.isValid ? null : validation.error;
  };

  const validationError = getValidationError();

  const handleConfirm = () => {
    onTimeSelect(selectedTime);
    onClose();
  };

  const handleCancel = () => {
    setSelectedTime(initialTime);
    onClose();
  };

  if (!isOpen) return null;

  // Generate hours (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  
  // Generate minutes (0 to 59)
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        ref={modalRef}
        className="bg-white rounded-xl p-6 w-80 max-w-sm mx-4"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Time Selection */}
          <div className="flex space-x-3">
            {/* Hours */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Hour</label>
              <select
                value={selectedTime.hours}
                onChange={(e) => handleTimeChange('hours', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {hours.map(hour => (
                  <option key={hour} value={hour}>
                    {hour.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>

            {/* Minutes */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Minute</label>
              <select
                value={selectedTime.minutes}
                onChange={(e) => handleTimeChange('minutes', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {minutes.map(minute => (
                  <option key={minute} value={minute}>
                    {minute.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>

            {/* Period */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
              <select
                value={selectedTime.period}
                onChange={(e) => handleTimeChange('period', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-800 text-sm">{validationError}</p>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              onClick={handleCancel}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!!validationError}
              className={`flex-1 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationError 
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimePicker;
